import { Injectable } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import { LeaveReasonsDashForm, LeaveReasonsDashboard, LeaveReasonsDashboardStore } from "@core/models/leave-reasons/leave-reasons-dashboard.model";
import { noChangesReplacer } from "@shared/helpers";
import { BehaviorSubject, Observable } from "rxjs";
import { pluck } from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class LeaveReasonsDashboardStoreService {
	private readonly _initialStoreData: LeaveReasonsDashboardStore = {
		leaveReasonsDash: null,
		sysText: null,
		selectedIndex: null,
		formInitValues: null,
		matSortData: null,
		pageData: null,
		dashForm: this.fb.group<LeaveReasonsDashForm>({
			searchValue: this.fb.control(null),
			employer: this.fb.control(null),
		})
	};

	private leaveReasonsDashboardStore: BehaviorSubject<LeaveReasonsDashboardStore>;
	private store$: Observable<LeaveReasonsDashboardStore>;
	
	private readonly templateNames = {
		leaveReasons: "leaveReasons",
    addNewLeaveReason: "addNewLeaveReason",
		deleteConfirmation: "deleteConfirmation",
		unsavedChanges: "unsavedChanges",
  };

	constructor(private fb: FormBuilder) { 
		this.leaveReasonsDashboardStore = new BehaviorSubject(this._initialStoreData);
		this.store$ = this.leaveReasonsDashboardStore.asObservable();
  }

	public get templates() {
    return this.templateNames;
  }

	public get store() {
    return this.leaveReasonsDashboardStore.value;
  }
  
  public get leaveReasonsDash$(): Observable<LeaveReasonsDashboard> {
    return this.store$.pipe(pluck('leaveReasonsDash'));
  }

  public get leaveReasonsDash() {
    return this.store.leaveReasonsDash;
  }

  public set leaveReasonsDash(leaveReasonsDash: LeaveReasonsDashboard) {
    const store = { ...this.store, leaveReasonsDash };
    this.leaveReasonsDashboardStore.next(store);
  }
  
  public get sysText() {
    return this.store.sysText;
  }
  
  public set sysText(sysText: any) {
    const store = { ...this.store, sysText };
    this.leaveReasonsDashboardStore.next(store);
  }

  public get selectedIndex() {
    return this.store.selectedIndex;
  }

  public set selectedIndex(selectedIndex: number) {
    this.store.selectedIndex = selectedIndex;
  }

  public get leaveReasonsForm(): FormGroup<LeaveReasonsDashForm> {
    return this.store.dashForm;
  }

  get leaveReasonsFormNoChanges(): boolean {
    return JSON.stringify(this.leaveReasonsForm.value, noChangesReplacer) === JSON.stringify(this.store.formInitValues, noChangesReplacer);
  }

  public set leaveReasonsSortData(sortData: Sort) {
    this.store.matSortData = sortData;
  }

  public get leaveReasonsSortData(): Sort {
    return this.store.matSortData;
  }

  public set leaveReasonsPageData(pageData: PageEvent) {
    this.store.pageData = pageData;
  }

  public get leaveReasonsPageData(): PageEvent {
    return this.store.pageData;
  }

  public leaveReasonAssignedToEmployer(leaveReasonId: number, assigned: boolean) {
    this.store.leaveReasonsDash.leaveReasonItems.find(lr => lr.id === leaveReasonId).assignedToEmployer = assigned;
  }

	public unloadStore() {
		const store: LeaveReasonsDashboardStore = {
			leaveReasonsDash: null,
			sysText: null,
			selectedIndex: null,
			formInitValues: null,
			matSortData: null,
			pageData: null,
			dashForm: this.fb.group<LeaveReasonsDashForm>({
				searchValue: this.fb.control(null),
				employer: this.fb.control(null),
			})
    }
    this.leaveReasonsDashboardStore.next(store)
  }
}